import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Lock } from "lucide-react";
import ZoneGrid from "@/components/schema/ZoneGrid";
import { optionValues } from "@/lib/schema";

const LABEL = "text-xs uppercase tracking-[0.15em] text-slate-600";

function isReadOnly(field, role) {
  if (role === "admin") return false;
  const r = role === "doctor" ? "therapist" : role;
  return (field.readOnlyFor || []).some((x) => (x === "doctor" ? "therapist" : x) === r);
}

// One field, rendered from its definition.
export function FieldInput({ field, value, onChange, role, patientData, disabled }) {
  const ro = disabled || isReadOnly(field, role);
  const unit = field.unit ? <span className="text-xs text-slate-400 ml-2">{field.unit}</span> : null;
  const [jsonError, setJsonError] = useState("");

  switch (field.type) {
    case "bool":
      return <Switch checked={!!value} disabled={ro} onCheckedChange={onChange} data-testid={`field-${field.key}`} />;

    case "int":
    case "float": {
      const step = field.type === "int" ? 1 : (field.max - field.min <= 2 ? 0.01 : 0.1);
      const unitless01 = field.type === "float" && field.min >= 0 && field.max <= 1 && !field.unit;
      if (field.widget === "slider" || unitless01) {
        const pct = field.display === "percent" || unitless01;
        const shown = pct ? Math.round((value ?? 0) * 100) : value;
        return (
          <div>
            <div className="text-sm font-medium tabular-nums mb-2">{shown}{pct ? "%" : ""}{unit}</div>
            <Slider disabled={ro} value={[value ?? field.min ?? 0]} min={field.min ?? 0} max={field.max ?? 1}
              step={pct ? 0.01 : step} onValueChange={(v) => onChange(v[0])} data-testid={`field-${field.key}`} />
          </div>
        );
      }
      if (field.widget === "segmented" || (field.type === "int" && field.min !== undefined && field.max - field.min <= 4)) {
        const opts = [];
        for (let i = field.min; i <= field.max; i += 1) opts.push(i);
        return (
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid={`field-${field.key}`}>
            {opts.map((n) => (
              <button key={n} type="button" disabled={ro} onClick={() => onChange(n)}
                className={`px-3 py-1.5 text-sm ${value === n ? "bg-purple-600 text-white" : "bg-white text-slate-600 hover:bg-purple-50"}`}>
                {n}
              </button>
            ))}
          </div>
        );
      }
      return (
        <div className="flex items-center">
          <Input type="number" disabled={ro} className="max-w-[140px]" step={step} min={field.min} max={field.max}
            value={value ?? ""} data-testid={`field-${field.key}`}
            onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} />
          {unit}
        </div>
      );
    }

    case "enum":
      return (
        <Select value={value ?? ""} disabled={ro} onValueChange={onChange}>
          <SelectTrigger className="max-w-[200px]" data-testid={`field-${field.key}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {optionValues(field, patientData).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      );

    case "flags":
      if (field.widget === "zoneGrid" || optionValues(field, patientData).join() === (patientData?.reachZones?.cells || []).join()) {
        const cells = optionValues(field, patientData);
        return <ZoneGrid value={value} onChange={onChange} cells={cells} groups={patientData?.reachZones?.groups} readOnly={ro} />;
      }
      return <Input disabled={ro} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="Comma separated" />;

    case "date":
      return <Input type="date" disabled={ro} className="max-w-[180px]" value={(value || "").slice(0, 10)}
        onChange={(e) => onChange(e.target.value)} data-testid={`field-${field.key}`} />;

    case "json":
      return (
        <div>
          <Textarea disabled={ro} rows={4} className="font-mono text-xs" value={value ?? ""} data-testid={`field-${field.key}`}
            onChange={(e) => {
              const v = e.target.value;
              try { if (v.trim()) JSON.parse(v); setJsonError(""); } catch (err) { setJsonError(err.message); }
              onChange(v);
            }} />
          {jsonError && <div className="text-xs text-red-600 mt-1">Invalid JSON: {jsonError}</div>}
        </div>
      );

    default: // string
      return <Input disabled={ro} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${field.key}`} />;
  }
}

// A list of fields (groups render as a titled block). `skip` hides keys handled elsewhere.
export default function FieldForm({ fields, values, onChange, role, patientData, skip = [], disabled, depth = 0 }) {
  return (
    <div className={`grid gap-5 ${depth === 0 ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
      {(fields || []).filter((f) => !skip.includes(f.key)).map((f) => {
        if (f.type === "group") {
          return (
            <div key={f.key} className="md:col-span-full border border-purple-100 rounded-xl p-4 bg-white/60">
              <div className="text-xs uppercase tracking-[0.15em] text-purple-700 mb-1">{f.label || f.key}</div>
              {f.help && <div className="text-[11px] text-slate-500 mb-3 max-w-xl">{f.help}</div>}
              <FieldForm fields={f.fields} values={values?.[f.key] || {}} role={role} patientData={patientData}
                disabled={disabled || isReadOnly(f, role)} depth={depth + 1}
                onChange={(sub) => onChange({ ...values, [f.key]: sub })} />
            </div>
          );
        }
        const wide = f.widget === "zoneGrid" || f.type === "json" || (f.type === "flags" && optionValues(f, patientData).length === 9);
        return (
          <div key={f.key} className={wide ? "md:col-span-full" : ""}>
            <Label className={LABEL}>
              {f.label || f.key}
              {isReadOnly(f, role) && <Lock className="inline h-3 w-3 ml-1 text-slate-400" />}
            </Label>
            {f.help && <div className="text-[11px] text-slate-500 mt-1 max-w-md">{f.help}</div>}
            <div className="mt-2">
              <FieldInput field={f} value={values?.[f.key]} role={role} patientData={patientData} disabled={disabled}
                onChange={(v) => onChange({ ...values, [f.key]: v })} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
